import {
  // BarChart3,
  Building2,
  // Calculator,
  History,
  LayoutDashboard,
  Package,
  Settings,
  ShoppingCart,
  Truck,
  Users,
  // Wallet,
  type LucideIcon,
} from 'lucide-react';
import { SUPER_ADMIN_ROLE } from '../app/context.ts';
import { AUDIT_READ_ROLE } from '../features/history/history.ts';
// import { FINANCE_READ_ROLE } from '../features/finance/finance.ts';
import type { OrderView } from '../features/orders/orders.ts';
import { RETURNS_ROLE } from '../features/returns/returns.ts';

export type NavPath =
  | '/'
  | '/orders'
  | '/customers'
  | '/products'
  | '/categories'
  | '/inventory'
  | '/products/labels'
  | '/handover'
  | '/returns'
  | '/returned-parcels'
  | '/missing-parcels'
  | '/finance'
  | '/finance/remittances'
  | '/finance/disputes'
  | '/finance/expenses'
  | '/calculator'
  | '/statistics/delivery'
  | '/statistics/confirmation'
  | '/statistics/products'
  | '/statistics/orders'
  | '/statistics/marketing'
  | '/settings'
  | '/settings/history'
  | '/tenants';

export type NavBadge = 'ordersToConfirm' | 'missingParcels';
export type NavBadgeCounts = Readonly<Partial<Record<NavBadge, number>>>;

const BADGE_VIEWS: Readonly<Record<NavBadge, OrderView>> = {
  ordersToConfirm: 'to_confirm',
  missingParcels: 'missing',
};

export const badgeCountOf = (target: { readonly badge?: NavBadge }, badges: NavBadgeCounts): number =>
  target.badge === undefined ? 0 : (badges[target.badge] ?? 0);

export const moduleBadgeCountOf = (module: NavModule, badges: NavBadgeCounts): number =>
  module.pages.reduce((total, page) => total + badgeCountOf(page, badges), 0);

export const badgeCountsOf = (viewCounts: ReadonlyMap<OrderView, number> | undefined): NavBadgeCounts =>
  Object.fromEntries(
    Object.entries(BADGE_VIEWS).flatMap(([badge, view]) => {
      const count = viewCounts?.get(view);
      return count === undefined ? [] : [[badge, count]];
    }),
  );

export type NavItem = {
  readonly id: string;
  readonly to: NavPath;
  readonly labelKey: string;
  readonly shortLabelKey?: string;
  readonly icon: LucideIcon;
  readonly role?: string;
  readonly badge?: NavBadge;
};

export type NavPage = Omit<NavItem, 'icon'>;

export type NavModule = {
  readonly id: string;
  readonly labelKey: string;
  readonly icon: LucideIcon;
  readonly role?: string;
  readonly pages: readonly NavPage[];
};

export type NavEntry =
  { readonly kind: 'item'; readonly item: NavItem } | { readonly kind: 'module'; readonly module: NavModule };

const itemEntry = (value: NavItem): NavEntry => ({ kind: 'item', item: value });
const moduleEntry = (value: NavModule): NavEntry => ({ kind: 'module', module: value });

export const navigation: readonly NavEntry[] = [
  itemEntry({ id: 'dashboard', to: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard }),
  itemEntry({
    id: 'orders',
    to: '/orders',
    labelKey: 'nav.orders',
    icon: ShoppingCart,
    badge: 'ordersToConfirm',
  }),
  itemEntry({ id: 'customers', to: '/customers', labelKey: 'nav.customers', icon: Users }),
  moduleEntry({
    id: 'catalog',
    labelKey: 'nav.products',
    icon: Package,
    pages: [
      { id: 'products', to: '/products', labelKey: 'nav.products' },
      { id: 'categories', to: '/categories', labelKey: 'nav.categories' },
      { id: 'inventory', to: '/inventory', labelKey: 'nav.inventory' },
    ],
  }),
  moduleEntry({
    id: 'logistics',
    labelKey: 'nav.group_logistics',
    icon: Truck,
    pages: [
      { id: 'handover', to: '/handover', labelKey: 'nav.handover', shortLabelKey: 'nav.handoverShort' },
      { id: 'returns', to: '/returns', labelKey: 'nav.returns', role: RETURNS_ROLE },
      { id: 'returned-parcels', to: '/returned-parcels', labelKey: 'nav.returnedParcels' },
      {
        id: 'missing-parcels',
        to: '/missing-parcels',
        labelKey: 'nav.missingParcels',
        badge: 'missingParcels',
      },
    ],
  }),
  // moduleEntry({
  //   id: 'finance',
  //   labelKey: 'nav.finance',
  //   icon: Wallet,
  //   role: FINANCE_READ_ROLE,
  //   pages: [
  //     { id: 'finance', to: '/finance', labelKey: 'nav.financeOverview' },
  //     { id: 'remittances', to: '/finance/remittances', labelKey: 'nav.remittances' },
  //     { id: 'disputes', to: '/finance/disputes', labelKey: 'nav.disputes' },
  //     { id: 'expenses', to: '/finance/expenses', labelKey: 'nav.expenses' },
  //   ],
  // }),
  // moduleEntry({
  //   id: 'statistics',
  //   labelKey: 'nav.statistics',
  //   icon: BarChart3,
  //   role: FINANCE_READ_ROLE,
  //   pages: [
  //     { id: 'statistics-delivery', to: '/statistics/delivery', labelKey: 'nav.statistics_delivery' },
  //     {
  //       id: 'statistics-confirmation',
  //       to: '/statistics/confirmation',
  //       labelKey: 'nav.statistics_confirmation',
  //     },
  //     { id: 'statistics-products', to: '/statistics/products', labelKey: 'nav.statistics_products' },
  //     { id: 'statistics-orders', to: '/statistics/orders', labelKey: 'nav.statistics_orders' },
  //     { id: 'statistics-marketing', to: '/statistics/marketing', labelKey: 'nav.statistics_marketing' },
  //   ],
  // }),
  // itemEntry({ id: 'calculator', to: '/calculator', labelKey: 'nav.calculator', icon: Calculator }),
];

export const pinnedNavigation: readonly NavItem[] = [
  { id: 'settings', to: '/settings', labelKey: 'nav.settings', icon: Settings },
  { id: 'tenants', to: '/tenants', labelKey: 'nav.tenants', icon: Building2, role: SUPER_ADMIN_ROLE },
];

export const commandOnlyDestinations: readonly NavItem[] = [
  { id: 'history', to: '/settings/history', labelKey: 'nav.history', icon: History, role: AUDIT_READ_ROLE },
];

export const selectionOnlyDestinations: readonly NavItem[] = [
  { id: 'labels', to: '/products/labels', labelKey: 'nav.labels', icon: Package },
];

const BOTTOM_NAV_IDS: readonly string[] = ['dashboard', 'orders', 'products', 'handover', 'customers'];

type RoleCheck = (role: string) => boolean;

const permitted =
  (hasRole: RoleCheck) =>
  (target: { readonly role?: string }): boolean =>
    target.role === undefined || hasRole(target.role);

const visibleEntry = (entry: NavEntry, hasRole: RoleCheck): NavEntry | null => {
  if (entry.kind === 'item') return permitted(hasRole)(entry.item) ? entry : null;
  if (!permitted(hasRole)(entry.module)) return null;
  const pages = entry.module.pages.filter(permitted(hasRole));
  return pages.length === 0 ? null : { kind: 'module', module: { ...entry.module, pages } };
};

export const visibleNavigation = (hasRole: RoleCheck): readonly NavEntry[] =>
  navigation.map((entry) => visibleEntry(entry, hasRole)).filter((entry) => entry !== null);

export const visiblePinned = (hasRole: RoleCheck): readonly NavItem[] =>
  pinnedNavigation.filter(permitted(hasRole));

export const flattenNavigation = (entries: readonly NavEntry[]): readonly NavItem[] =>
  entries.flatMap((entry) =>
    entry.kind === 'item'
      ? [entry.item]
      : entry.module.pages.map((page) => ({ ...page, icon: entry.module.icon })),
  );

export const bottomNavigation = (entries: readonly NavEntry[]): readonly NavItem[] => {
  const byId = new Map(flattenNavigation(entries).map((link) => [link.id, link]));
  return BOTTOM_NAV_IDS.map((id) => byId.get(id)).filter((link) => link !== undefined);
};

export const visibleDestinations = (hasRole: RoleCheck): readonly NavItem[] => [
  ...flattenNavigation(visibleNavigation(hasRole)),
  ...visiblePinned(hasRole),
  ...commandOnlyDestinations.filter(permitted(hasRole)),
];

export const titledDestinations = (destinations: readonly NavItem[]): readonly NavItem[] => [
  ...destinations,
  ...selectionOnlyDestinations,
];

const coversPath = (link: { readonly to: NavPath }, pathname: string): boolean =>
  link.to === '/' ? pathname === '/' : pathname === link.to || pathname.startsWith(`${link.to}/`);

export const moduleOfPath = (entries: readonly NavEntry[], pathname: string): string | null => {
  const owner = entries.find(
    (entry) => entry.kind === 'module' && entry.module.pages.some((page) => coversPath(page, pathname)),
  );
  return owner?.kind === 'module' ? owner.module.id : null;
};

const deeperOf = (best: NavItem | null, link: NavItem): NavItem =>
  best === null || link.to.length > best.to.length ? link : best;

export const pageTitleKeyOf = (destinations: readonly NavItem[], pathname: string): string | null =>
  destinations.filter((link) => coversPath(link, pathname)).reduce<NavItem | null>(deeperOf, null)
    ?.labelKey ?? null;
